import React, { useState } from "react";
import {
  Box,
  Stack,
  Card,
  CardContent,
  IconButton,
  Typography,
  Tooltip,
  Modal,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  ReportProblem as ReportProblemIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { Power as PowerIcon } from "@mui/icons-material";
import { convertDateToText } from "../utils";

function MyBookingsList({
  t,
  myBookings,
  loadingBookings,
  availableDates,
  today,
  openConfirmDialog,
  openReportDialog,
  viewReportDetails,
  openAssignmentReportDialog,
  assignmentReports,
}) {
  const [selectedImage, setSelectedImage] = useState(null);

  const handleImageView = (slotNumber) => {
    setSelectedImage(`/slots/${slotNumber}.png`);
  };

  const handleCloseModal = () => {
    setSelectedImage(null);
  };

  if (loadingBookings) {
    return null;
  }

  if (!myBookings.length) {
    return (
      <Typography variant="h6" mt={2} color="text.secondary">
        {t("bookings.no_bookings")}
      </Typography>
    );
  }

  const sortedBookings = [...myBookings].sort((a, b) => {
    if (!a.report && b.report) return -1;
    if (a.report && !b.report) return 1;
    return new Date(a.date) - new Date(b.date);
  });

  return (
    <Stack spacing={2} mt={2}>
      <Typography variant="h6" mt={2}>
        {t("bookings.your_bookings")}
      </Typography>
      {sortedBookings.map((booking) => (
        <Card
          key={booking.id}
          sx={{
            border: booking.report
              ? "2px solid red"
              : booking.date === availableDates.next_day ||
                booking.date === availableDates.second_day
              ? "2px solid #05f50d"
              : "1px solid #e0e0e0",
            padding: 1,
          }}
        >
          <CardContent sx={{ position: "relative" }}>
            <Box
              sx={{
                position: "absolute",
                top: 4,
                right: 4,
                display: "flex",
                gap: 1,
              }}
            >
              {booking.report ? (
                <Tooltip title={t("report.view_report")}>
                  <IconButton
                    onClick={() => viewReportDetails(booking.report)}
                    sx={{ color: "error.main" }}
                    aria-label="View Report"
                  >
                    <ReportProblemIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <>
                  {booking.date === today && typeof booking.id === 'number' && (
                    <Tooltip title={t("bookings.report_problem")}>
                      <IconButton
                        onClick={() => openReportDialog(booking.id)}
                        aria-label="Report Problem"
                      >
                        <ReportProblemIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {typeof booking.id === 'string' && booking.id.startsWith('assigned-') && 
                   !assignmentReports?.some(report => report.date === booking.date) && (
                    <Tooltip title={t("assignment_report.create_report")}>
                      <IconButton
                        onClick={() => openAssignmentReportDialog(booking.date)}
                        sx={{ color: "warning.main" }}
                        aria-label="Create Assignment Report"
                      >
                        <ReportProblemIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              )}
              {!booking.report && (
                <Tooltip title={t("actions.cancel")}>
                  <IconButton
                    onClick={() => openConfirmDialog(booking.id)}
                    sx={{ color: "error.main" }}
                    aria-label="Delete Booking"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {(booking.date === availableDates.next_day ||
              booking.date === availableDates.second_day) && (
              <Typography
                variant="h6"
                sx={{
                  color: booking.report ? "red" : "#00af07",
                  fontWeight: "bold",
                  mb: 1,
                }}
              >
                {convertDateToText(booking.date, t).toUpperCase() ||
                  "Loading..."}
              </Typography>
            )}
            <Typography variant="subtitle1">
              <strong>{t("general.date")}:</strong> {booking.date}
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <strong>{t("slots.slot")}:</strong> {booking.slot_number}
              {booking.slot_has_charger && (
                <PowerIcon sx={{ color: "primary.main" }} />
              )}
              <Tooltip title={t("slots.view_image")}>
                <IconButton
                  size="small"
                  onClick={() => handleImageView(booking.slot_number)}
                >
                  <VisibilityIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            </Typography>
            <Typography variant="subtitle2">
              <strong>{t("general.address")}:</strong> {booking.address}
            </Typography>
          </CardContent>
        </Card>
      ))}

      <Modal
        open={Boolean(selectedImage)}
        onClose={handleCloseModal}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            position: "relative",
            maxWidth: "90vw",
            maxHeight: "90vh",
            bgcolor: "background.paper",
            borderRadius: 1,
            boxShadow: 24,
            p: 1,
          }}
        >
          <IconButton
            onClick={handleCloseModal}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "grey.500",
              bgcolor: "background.paper",
              "&:hover": {
                bgcolor: "grey.100",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          <img
            src={selectedImage}
            alt="Parking Slot"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
            }}
          />
        </Box>
      </Modal>
    </Stack>
  );
}

export default MyBookingsList;
